import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Grid,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
} from '@mui/material';
import { CustomTextInput } from 'components/shared';
import Buttons from 'components/elements/Buttons';
import { strings } from 'i18n/Strings';
import { debounce } from 'lodash';
import {
  displayEmployeeInfo,
  register,
  registerOrgEmployee,
  updateEmployee,
  updateOrganizationEmployee,
} from 'service';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import type { RootState } from 'store/rootReducer';
import MuiPhoneNumber from 'material-ui-phone-number';
import { DatePicker } from '@mui/x-date-pickers';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import type { GridValidRowModel } from '@mui/x-data-grid';
import {
  pepTypeOptions,
  genderOptions,
  maritalStatusOptions,
  commonConstantTexts,
} from 'constants/constantText';
import { parsePhoneNumberFromString } from 'libphonenumber-js';

export interface ICreateEmployeeProps {
  onClose: () => void;
  updateEmployeeId?: string;
  updateEmployeeData: () => void;
  organizationId?: string | undefined;
  page?: string;
  from?: string;
  selectedData?: IEmployeeInfo;
  isRegistered?: boolean;
  hidePhoneField?: boolean;
}

export interface IEmployeeInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  employeeId?: string;
  designation: string;
  department: string;
  location: string;
  startDate: Dayjs | null;
  endDate?: Dayjs | null;
  emailSubscription: boolean;
  empHasRoles?: GridValidRowModel;
  roleId?: number;
  orgAdminId?: number;
  economicStatus: number | null;
  economicOccupation: number | null;
  employeeContribution: string | null;
  employerContribution: string | null;
  employeeContributionPercentage: string | null;
  employerContributionPercentage: string | null;
  gender: string;
  maritalStatus: string;
  pepType?: boolean;
  employmentCategoryInfo: {
    id: number;
    parentId?: number;
  };
  status: string;
}

export interface IErrorMessages {
  firstNameError?: string;
  lastNameError?: string;
  employeeContributionError?: string;
  employerContributionError?: string;
  employeeContributionPercentageError?: string;
  employerContributionPercentageError?: string;
  emailError?: string;
  phoneNumberError?: string;
  employeeIdError?: string;
  pepTypeError?: boolean;
}

const CreateEmployee = (props: ICreateEmployeeProps): JSX.Element => {
  const dispatch = useDispatch();
  const {
    onClose,
    updateEmployeeId,
    updateEmployeeData,
    organizationId,
    from,
    selectedData,
    hidePhoneField,
  } = props;
  const { orgId, empId } = useSelector((state: RootState) => state.onboarding);
  const { economicStatusAndOccupation } = useSelector(
    (state: RootState) => state.employeeDetails,
  );

  const [dataLoader, setDataLoader] = useState(false);
  const [employeeInfo, setEmployeeInfo] = useState<IEmployeeInfo>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    employeeId: '',
    designation: '',
    department: '',
    location: '',
    employeeContribution: null,
    employerContribution: null,
    employeeContributionPercentage: null,
    employerContributionPercentage: null,
    startDate: null,
    endDate: null,
    emailSubscription: true,
    economicStatus: null,
    economicOccupation: null,
    gender: '',
    maritalStatus: '',
    pepType: false,
    employmentCategoryInfo: { id: 0, parentId: undefined },
    status: '',
  });
  const [updateEmail, setUpdateEmail] = useState<string>('');
  const [storedEmailSubscription, setStoredEmailSubscription] = useState(true);

  const [loader, setLoader] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (updateEmployeeId) {
      if (from === 'admin' && selectedData) {
        const { employmentCategoryInfo, gender, maritalStatus, pepType } =
          selectedData.organizationEmployee;
        const { startDate, endDate } = selectedData;
        setEmployeeInfo({
          ...selectedData,
          gender,
          maritalStatus,
          pepType,
          economicOccupation:
            employmentCategoryInfo?.parentId !== null
              ? employmentCategoryInfo?.id
              : 0,
          economicStatus:
            employmentCategoryInfo?.parentId ?? employmentCategoryInfo?.id,
          startDate: startDate && dayjs(startDate),
          endDate: endDate && dayjs(endDate),
        });
      } else if (selectedData?.orgAdminId) {
        setIsAdmin(true);
        const { employmentCategoryInfo, gender, maritalStatus, pepType } =
          selectedData;
        const { startDate, endDate } = selectedData;
        setEmployeeInfo({
          ...selectedData,
          gender,
          maritalStatus,
          pepType,
          economicOccupation:
            employmentCategoryInfo?.parentId !== null
              ? employmentCategoryInfo?.id
              : 0,
          economicStatus:
            employmentCategoryInfo?.parentId ?? employmentCategoryInfo?.id,
          startDate: startDate && dayjs(startDate),
          endDate: endDate && dayjs(endDate),
        });
      } else {
        getEmployeeDetails();
      }
    }
  }, []);

  const [employeeInfoError, setEmployeeInfoError] = useState<IErrorMessages>({
    firstNameError: '',
    lastNameError: '',
    emailError: '',
    phoneNumberError: '',
    employeeIdError: '',
    employeeContributionError: '',
    employerContributionError: '',
    pepTypeError: undefined,
  });
  const getEmployeeDetails = (): void => {
    setDataLoader(true);
    if (updateEmployeeId) {
      displayEmployeeInfo(updateEmployeeId)
        .then((res) => {
          if (typeof res !== 'undefined') {
            const { details } = res as { details: IEmployeeInfo };

            if (details) {
              const { employmentCategoryInfo } = details;
              setEmployeeInfo({
                ...details,
                startDate: details.startDate && dayjs(details.startDate),
                endDate: details.endDate && dayjs(details.endDate),
                economicOccupation:
                  employmentCategoryInfo?.parentId !== null
                    ? employmentCategoryInfo?.id
                    : 0,
                economicStatus:
                  employmentCategoryInfo?.parentId ??
                  employmentCategoryInfo?.id,
              });
              setUpdateEmail(details?.email);
              setStoredEmailSubscription(details?.emailSubscription);
              setDataLoader(false);
            }
          }
        })
        .catch((err: Error) => {
          throw err;
        });
    }
  };

  const handlePhoneChange = (value: string): void => {
    setEmployeeInfo((prev) => {
      return { ...prev, phone: value };
    });
  };

  const handleStartDate = (value: Dayjs | null): void => {
    setEmployeeInfo((prev) => {
      return {
        ...prev,
        startDate: value,
      };
    });
  };
  const handleInputChange = (
    type: string,
    e: { target: { value: string } },
  ): '' | undefined => {
    let newValue;

    if (
      [
        'employeeContribution',
        'employerContribution',
        'employeeContributionPercentage',
        'employerContributionPercentage',
      ].includes(type)
    ) {
      // Validate and allow only numbers with up to two decimal places
      const value = e.target.value;
      const formattedValue = value.match(/^\d*\.?\d{0,2}$/)
        ? value
        : employeeInfo[type];

      newValue = { [type]: formattedValue };
    } else if (type === 'economicStatus') {
      newValue = { [type]: e.target.value, economicOccupation: null };
    } else {
      newValue = { [type]: e.target.value };
    }

    setEmployeeInfo((prev) => ({ ...prev, ...newValue }));
  };

  const handlePhoneValidation = (value: string): void => {
    if (hidePhoneField) return;
    const trimmedValue = value?.trim();
    if (!trimmedValue || trimmedValue.length === 0 || trimmedValue === '+353') {
      setEmployeeInfoError((prev) => ({
        ...prev,
        phoneNumberError: '',
      }));
      return;
    }
    const phoneNumber = parsePhoneNumberFromString(value);
    const isValid = phoneNumber?.isValid();
    setEmployeeInfoError((prev) => ({
      ...prev,
      phoneNumberError: isValid ? '' : strings.displayText.phoneNumberIsInvalid,
    }));
  };

  const handleValidation = (
    type: string,
    e: { target: { value: string } },
  ): void => {
    switch (type) {
      case 'firstName': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              firstNameError: strings.displayText.firstNameRequiredText,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              firstNameError: '',
            };
          });
        return;
      }
      case 'lastName': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              lastNameError: strings.displayText.lastNameRequiredText,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              lastNameError: '',
            };
          });
        return;
      }
      case 'email': {
        const emailRegex =
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return { ...prev, emailError: strings.displayText.emailEmptyText };
          });
        else if (!emailRegex.test(e.target.value))
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              emailError: strings.displayText.invalidEmailText,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return { ...prev, emailError: '' };
          });

        return;
      }
      case 'pepType': {
        setEmployeeInfoError((prev) => {
          return {
            ...prev,
            pepTypeError:
              e.target.value === undefined
                ? strings.displayText.pepTypeRequiredText
                : undefined,
          };
        });
        return;
      }

      case 'employeeId': {
        setEmployeeInfoError((prev) => ({
          ...prev,
          employeeIdError: '',
        }));
        return;
      }
      case 'employeeContribution': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employeeContributionError:
                strings.displayText.employeeContribution,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employeeContributionError: '',
            };
          });
        return;
      }
      case 'employerContribution': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employerContributionError:
                strings.displayText.employerContribution,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employerContributionError: '',
            };
          });
        return;
      }
      case 'employeeContributionPercentage': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employeeContributionPercentageError:
                strings.displayText.employeeContributionPercentage,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employeeContributionPercentageError: '',
            };
          });
        return;
      }
      case 'employerContributionPercentage': {
        if (e.target.value === '')
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employerContributionPercentageError:
                strings.displayText.employerContributionPercentage,
            };
          });
        else
          setEmployeeInfoError((prev) => {
            return {
              ...prev,
              employerContributionPercentageError: '',
            };
          });
      }
    }
  };

  const getErrorFieldName = (fieldName: string): string => {
    if (fieldName === 'firstName') {
      return strings.displayText.firstName;
    } else if (fieldName === 'lastName') {
      return strings.displayText.lastName;
    } else if (fieldName === 'employeeId') {
      return strings.displayText.employeeID;
    } else if (fieldName === 'email') {
      return strings.displayText.email;
    } else if (fieldName === 'employeeContribution') {
      return strings.displayText.employeeContribution;
    } else if (fieldName === 'employerContribution') {
      return strings.displayText.employerContribution;
    } else if (fieldName === 'employeeContributionPercentage') {
      return strings.displayText.employeeContributionPercentage;
    } else if (fieldName === 'employerContributionPercentage') {
      return strings.displayText.employerContributionPercentage;
    } else if (fieldName === 'gender') {
      return strings.displayText.gender;
    } else if (fieldName === 'marital Status') {
      return strings.displayText.maritalStatus;
    } else {
      return strings.displayText.mandatoryFields;
    }
  };

  /**
   * Validates form data during submission by checking required fields
   * @param submitData - The form data to validate
   * @returns True if validation passes, false otherwise
   */
  const handleValidationDuringSubmit = (
    submitData?: GridValidRowModel,
  ): boolean => {
    if (submitData) {
      const keys: string[] = Object.keys(submitData);
      let validate = true;
      for (let fieldIndex = 0; fieldIndex < keys.length; fieldIndex++) {
        const fieldName = keys[fieldIndex].split(/(?=[A-Z])/);
        const fieldNameStr: string = fieldName.join(' ');
        if (
          fieldNameStr === 'designation' ||
          fieldNameStr === 'department' ||
          fieldNameStr === 'location' ||
          fieldNameStr === 'phone' ||
          fieldNameStr === 'employee Id' ||
          fieldNameStr === 'economic Occupation' ||
          fieldNameStr === 'start Date' ||
          fieldNameStr === 'end Date' ||
          fieldNameStr === 'organization Id' ||
          fieldNameStr === 'role Id' ||
          fieldNameStr === 'role' ||
          fieldNameStr === 'admin Id' ||
          fieldNameStr === 'org Admin Id' ||
          fieldNameStr === 'email Subscription'
        ) {
          continue;
        }

        if (fieldNameStr === 'economic Status') {
          if (
            employeeInfo.economicOccupation === null &&
            employeeInfo.economicStatus === null
          ) {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillEconomicStatus,
              }),
            );
            validate = false;
            break;
          }
          if (
            employeeInfo.economicStatus !== 12 &&
            employeeInfo.economicOccupation === null
          ) {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillEconomicOccupation,
              }),
            );
            validate = false;
            break;
          }
          continue;
        }

        if (fieldNameStr === 'gender') {
          if (!employeeInfo.gender || employeeInfo.gender === '') {
            const fieldNameError = getErrorFieldName('gender');
            dispatch(
              setSnackBarFailed({
                snackBarMessage: `Kindly fill the ${fieldNameError}`,
              }),
            );
            validate = false;
            break;
          }
          continue;
        }

        if (fieldNameStr === 'marital Status') {
          if (
            !employeeInfo.maritalStatus ||
            employeeInfo.maritalStatus === ''
          ) {
            const fieldNameError = getErrorFieldName('marital Status');
            dispatch(
              setSnackBarFailed({
                snackBarMessage: `Kindly fill the ${fieldNameError}`,
              }),
            );
            validate = false;
            break;
          }
          continue;
        }

        if (fieldNameStr === 'pep Type') {
          if (
            employeeInfo.pepType === undefined ||
            employeeInfo.pepType === null
          ) {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillPepType,
              }),
            );
            validate = false;
            break;
          }
          continue;
        }
        if (fieldNameStr === 'phone') {
          if (hidePhoneField) continue;

          const trimmedPhone = employeeInfo.phone?.trim();
          if (trimmedPhone === '') {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: strings.displayText.fillPhoneNumber,
              }),
            );
            validate = false;
            break;
          }
          continue;
        }

        const fieldValue = submitData?.[keys[fieldIndex]];
        if (
          fieldValue === '' ||
          fieldValue === null ||
          fieldValue === undefined
        ) {
          const fieldNameError = getErrorFieldName(fieldNameStr);
          dispatch(
            setSnackBarFailed({
              snackBarMessage: `Kindly fill the ${fieldNameError}`,
            }),
          );
          validate = false;
          break;
        }
      }
      return validate;
    }
    return true;
  };

  const debounceHandler = useCallback(debounce(handleValidation, 300), []);

  const formatPhoneForSave = (
    phone: string | undefined | null,
  ): string | null => {
    const trimmed = phone?.trim() ?? '';
    // If phone is empty or just country code, treat as blank
    return trimmed === '' || trimmed === '+353' ? '' : trimmed;
  };
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    const formData = new FormData(event.currentTarget);
    const isRegistered = employeeInfo.status === commonConstantTexts.registered;
    const value = {
      firstName: isRegistered
        ? employeeInfo.firstName
        : formData.get('firstName')?.toString() ?? '',
      lastName: isRegistered
        ? employeeInfo.lastName
        : formData.get('lastName')?.toString() ?? '',
      email: isRegistered
        ? employeeInfo.email
        : formData.get('email')?.toString() ?? '',
      phone: isRegistered
        ? formatPhoneForSave(employeeInfo.phone)
        : formatPhoneForSave(formData.get('phoneNumber')?.toString()),
      employeeId: formData.get('employeeId')?.toString(),
      designation: formData.get('designation')?.toString(),
      department: formData.get('department')?.toString(),
      location: formData.get('location')?.toString(),
      employeeContribution: formData.get('employeeContribution')?.toString(),
      employerContribution: formData.get('employerContribution')?.toString(),
      employeeContributionPercentage: formData
        .get('employeeContributionPercentage')
        ?.toString(),
      employerContributionPercentage: formData
        .get('employerContributionPercentage')
        ?.toString(),
      startDate:
        employeeInfo.startDate === null
          ? null
          : dayjs(employeeInfo?.startDate).format('YYYY-MM-DD'),
      endDate:
        employeeInfo.endDate === null
          ? null
          : dayjs(employeeInfo?.endDate).format('YYYY-MM-DD'),
      organizationId: orgId || organizationId,
      roleId: from === strings.displayText.admin || isAdmin ? 3 : 4,
      role:
        from === strings.displayText.admin || isAdmin
          ? strings.displayText.organizationAdmin
          : strings.displayText.organizationEmployee,
      adminId: empId,
      orgAdminId: selectedData?.orgAdminId,
      emailSubscription:
        !updateEmployeeId ||
        updateEmail !== formData.get('email')?.toString() ||
        storedEmailSubscription,
      gender: employeeInfo.gender,
      economicStatus:
        economicStatusAndOccupation?.categories?.find(
          (category) => category.id === employeeInfo.economicStatus,
        )?.subCategories?.length > 0
          ? employeeInfo.economicOccupation
          : employeeInfo.economicStatus,
      maritalStatus: employeeInfo.maritalStatus,
      pepType: employeeInfo.pepType,
    };
    const submitCheck = handleValidationDuringSubmit(value);
    if (!submitCheck) {
      return;
    }
    if (updateEmployeeId) {
      void (async () => {
        setLoader(true);
        const updateFun =
          from === strings.displayText.admin || isAdmin
            ? updateEmployee
            : updateOrganizationEmployee;
        await updateFun(
          isAdmin ? selectedData.orgAdminId : parseInt(updateEmployeeId),
          value,
        )
          .then(() => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage:
                  from === strings.displayText.admin
                    ? strings.displayText.adminUpdatedSuccessText
                    : strings.displayText.employeeUpdatedSuccessText,
              }),
            );
            updateEmployeeData();
            setLoader(false);
            onClose();
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({
                snackBarMessage: err.response.data.msg,
              }),
            );
            setLoader(false);
          });
      })();
    } else
      void (async () => {
        const registerFun =
          from === strings.displayText.admin || isAdmin
            ? register
            : registerOrgEmployee;
        setLoader(true);
        await registerFun({ ...value })
          .then(() => {
            dispatch(
              setSnackBarSuccess({
                snackBarMessage:
                  from === strings.displayText.admin
                    ? strings.displayText.adminCreatedSuccessText
                    : strings.displayText.employeeCreatedSuccessText,
              }),
            );
            updateEmployeeData();
            setLoader(false);
            onClose();
          })
          .catch((err) => {
            dispatch(
              setSnackBarFailed({ snackBarMessage: err.response.data.msg }),
            );
            setLoader(false);
          });
      })();
  };

  const isDisabled =
    employeeInfoError.emailError !== '' ||
    employeeInfoError.firstNameError !== '' ||
    employeeInfoError.lastNameError !== '' ||
    employeeInfoError.phoneNumberError !== '' ||
    employeeInfoError.employeeIdError !== '' ||
    loader;

  return (
    <Box sx={{ maxWidth: '80rem' }}>
      {dataLoader ? (
        <Box
          sx={{
            width: '53rem',
            height: '25rem',
            textAlign: 'center',
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          <CircularProgress color="inherit" />
        </Box>
      ) : (
        <Box
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleSubmit}
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CustomTextInput
                value={employeeInfo.firstName}
                autoFocus={true}
                isFirstChild={true}
                required={true}
                label={'First Name'}
                name={'firstName'}
                onChange={(e) => {
                  handleInputChange('firstName', e);
                  debounceHandler('firstName', e);
                }}
                disabled={
                  employeeInfo.status === commonConstantTexts.registered
                }
                helperText={employeeInfoError?.firstNameError}
              />
            </Grid>
            <Grid item xs={6}>
              <CustomTextInput
                value={employeeInfo.lastName}
                required={true}
                label={'Last Name'}
                name={'lastName'}
                onChange={(e) => {
                  handleInputChange('lastName', e);
                  handleValidation('lastName', e);
                }}
                disabled={
                  employeeInfo.status === commonConstantTexts.registered
                }
                helperText={employeeInfoError?.lastNameError}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={7}>
              <CustomTextInput
                value={employeeInfo.email}
                required={true}
                label={'Email Address'}
                name={'email'}
                onChange={(e) => {
                  handleInputChange('email', e);
                  handleValidation('email', e);
                }}
                disabled={
                  employeeInfo.status === commonConstantTexts.registered
                }
                helperText={employeeInfoError?.emailError}
              />
            </Grid>
            <Grid item xs={5}>
              {hidePhoneField ? (
                <CustomTextInput
                  value={employeeInfo.employeeId}
                  label={'Employee ID'}
                  name={'employeeId'}
                  onChange={(e) => {
                    handleInputChange('employeeId', e);
                    handleValidation('employeeId', e);
                  }}
                  helperText={employeeInfoError?.employeeIdError}
                />
              ) : (
                <MuiPhoneNumber
                  countryCodeEditable={false}
                  label={'Phone Number'}
                  name={'phoneNumber'}
                  defaultCountry={'ie'}
                  value={employeeInfo.phone}
                  onChange={(value: string) => {
                    handlePhoneChange(value);
                    handlePhoneValidation(value);
                  }}
                  disabled={
                    employeeInfo.status === commonConstantTexts.registered
                  }
                  variant="outlined"
                  fullWidth
                  helperText={employeeInfoError?.phoneNumberError}
                  sx={{
                    '& p': {
                      color: 'red',
                    },
                    my: 2,
                  }}
                />
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            {!hidePhoneField && (
              <Grid item xs={3}>
                <CustomTextInput
                  value={employeeInfo.employeeId}
                  label={'Employee ID'}
                  name={'employeeId'}
                  onChange={(e) => {
                    handleInputChange('employeeId', e);
                    handleValidation('employeeId', e);
                  }}
                  helperText={employeeInfoError?.employeeIdError}
                />
              </Grid>
            )}
            <Grid item xs={hidePhoneField ? 6 : 4}>
              <CustomTextInput
                value={employeeInfo.designation}
                label={'Role/Designation'}
                name={'designation'}
                onChange={(e) => {
                  handleInputChange('designation', e);
                  handleValidation('designation', e);
                }}
              />
            </Grid>
            <Grid item xs={hidePhoneField ? 6 : 5}>
              <CustomTextInput
                value={employeeInfo.department}
                label={'Department'}
                name={'department'}
                onChange={(e) => {
                  handleInputChange('department', e);
                  handleValidation('department', e);
                }}
              />
            </Grid>
          </Grid>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullwidth>
                <InputLabel id="gender">Gender*</InputLabel>
                <Select
                  label={'Gender'}
                  name={'gender'}
                  required={true}
                  value={employeeInfo.gender}
                  onChange={(e) => {
                    handleInputChange('gender', e);
                    handleValidation('gender', e);
                  }}
                  style={{ width: '425px' }}
                >
                  {genderOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6}>
              <FormControl fullwidth>
                <InputLabel id="maritalStatus">Marital Status*</InputLabel>
                <Select
                  label={'Marital Status'}
                  name={'marital status'}
                  required={true}
                  value={employeeInfo.maritalStatus}
                  onChange={(e) => handleInputChange('maritalStatus', e)}
                  style={{ width: '425px' }}
                >
                  {maritalStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CustomTextInput
                type="number"
                value={employeeInfo.employeeContribution}
                required={true}
                label={'Employee Contribution'}
                name={'employeeContribution'}
                onChange={(e) => {
                  handleInputChange('employeeContribution', e);
                  handleValidation('employeeContribution', e);
                }}
                helperText={employeeInfoError?.employeeContributionError}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextInput
                type="number"
                value={employeeInfo.employerContribution}
                required={true}
                label={'Employer Contribution'}
                name={'employerContribution'}
                onChange={(e) => {
                  handleInputChange('employerContribution', e);
                  handleValidation('employerContribution', e);
                }}
                helperText={employeeInfoError?.employerContributionError}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <CustomTextInput
                type="number"
                value={employeeInfo.employeeContributionPercentage}
                required={true}
                label={'Employee Contribution%'}
                name={'employeeContributionPercentage'}
                onChange={(e) => {
                  handleInputChange('employeeContributionPercentage', e);
                  handleValidation('employeeContributionPercentage', e);
                }}
                helperText={employeeInfoError?.employeeContributionError}
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <CustomTextInput
                type="number"
                value={employeeInfo.employerContributionPercentage}
                required={true}
                label={'Employer Contribution%'}
                name={'employerContributionPercentage'}
                onChange={(e) => {
                  handleInputChange('employerContributionPercentage', e);
                  handleValidation('employerContributionPercentage', e);
                }}
                helperText={employeeInfoError?.employerContributionError}
                inputProps={{ min: 0 }}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl key={employeeInfo.economicStatus} fullWidth>
                <InputLabel id="economicStatus">Economic Status*</InputLabel>
                <Select
                  labelId="economicStatus"
                  value={employeeInfo.economicStatus}
                  onChange={(e) => handleInputChange('economicStatus', e)}
                  label="Economic status"
                  style={{ width: '425px' }}
                  name="economicStatus"
                >
                  {economicStatusAndOccupation?.categories?.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.categoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {employeeInfo.economicStatus &&
              economicStatusAndOccupation?.categories?.find(
                (category) => category.id === employeeInfo.economicStatus,
              )?.subCategories?.length > 0 && (
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="economicOccupation">
                      Economic Occupation*
                    </InputLabel>
                    <Select
                      labelId="economicOccupation"
                      value={employeeInfo.economicOccupation}
                      onChange={(e) =>
                        handleInputChange('economicOccupation', e)
                      }
                      label="Economic occupation"
                      style={{ width: '425px' }}
                      name="economicOccupation"
                    >
                      {economicStatusAndOccupation?.categories
                        .find(
                          (category) =>
                            category.id === employeeInfo.economicStatus,
                        )
                        ?.subCategories?.map((subcategory) => (
                          <MenuItem key={subcategory.id} value={subcategory.id}>
                            {subcategory.categoryName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <Grid item xs={5}>
                    {' '}
                  </Grid>
                  <Grid item xs={12} style={{ marginTop: '16px' }}></Grid>
                </Grid>
              )}

            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel id="pepType">
                  {strings.displayText.pepTypeText}
                </InputLabel>
                <Select
                  label={strings.displayText.pepTypeText}
                  name={'pepType'}
                  required={true}
                  value={employeeInfo.pepType ?? ''}
                  onChange={(e) => {
                    handleInputChange('pepType', e);
                    handleValidation('pepType', e);
                  }}
                  helperText={employeeInfoError?.pepTypeError}
                >
                  {pepTypeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} style={{ marginTop: '3px' }}></Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <CustomTextInput
                  value={employeeInfo.location}
                  label={'Location'}
                  name={'location'}
                  onChange={(e) => {
                    handleInputChange('location', e);
                    handleValidation('location', e);
                  }}
                />
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <DatePicker
                  sx={{ my: 2 }}
                  onChange={(value) => {
                    handleStartDate(value);
                  }}
                  value={employeeInfo.startDate}
                  label="Start Date"
                />
              </FormControl>
            </Grid>
          </Grid>
          <Buttons
            disabled={isDisabled}
            style={{ py: '0.8rem', mt: '1rem' }}
            type="submit"
            fullWidth={true}
            endIcon={loader ? <CircularProgress size={15} /> : null}
          >
            {'Save'}
          </Buttons>
        </Box>
      )}
    </Box>
  );
};

export default CreateEmployee;
